import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { AccountShell } from '../../components/account/AccountShell'
import { SettingsSection } from '../../components/knowledge/settings/SettingsSection'
import { Button, Text } from '../../components/ui'
import {
  cancelSubscription,
  formatKoboAsNgn,
  formatSubscriptionStatus,
  getSubscription,
  initiateCheckout,
  reactivateSubscription,
  type BillingCycle,
  type PaidPlan,
  type Subscription,
} from '../../lib/billing-api'
import { isWorkspaceAdmin, type Workspace } from '../../lib/api'
import { useWorkspaces } from '../../hooks/useWorkspaces'
import { authClient } from '../../lib/auth-client'
import { formatPlanLabel, PLAN_PRICING } from '../../lib/plan-limits'
import { cn } from '../../lib/cn'

const salesEmail = 'hello@usesondersoft.com'

const ANNUAL_SAVINGS = {
  growth: '₦58,000',
  business: '₦150,000',
} as const

const PLAN_AMOUNTS_DISPLAY = {
  growth: { monthlyKobo: 2_900_000, annualKobo: 29_000_000 },
  business: { monthlyKobo: 7_500_000, annualKobo: 75_000_000 },
} as const

type BillingSearch = {
  workspaceId?: string
}

export const Route = createFileRoute('/billing/')({
  validateSearch: (search: Record<string, unknown>): BillingSearch => ({
    workspaceId: typeof search.workspaceId === 'string' ? search.workspaceId : undefined,
  }),
  beforeLoad: async () => {
    const { data: session } = await authClient.getSession()
    if (!session) {
      throw redirect({ to: '/login' })
    }
    return { session }
  },
  component: BillingPage,
})

function BillingPage() {
  const navigate = useNavigate()
  const { workspaceId: searchWorkspaceId } = Route.useSearch()
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | null>(
    searchWorkspaceId ?? null,
  )
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly')
  const [checkoutError, setCheckoutError] = useState<string | null>(null)
  const [checkoutLoading, setCheckoutLoading] = useState<PaidPlan | null>(null)
  const [actionLoading, setActionLoading] = useState<'cancel' | 'reactivate' | null>(null)
  const [actionMessage, setActionMessage] = useState<string | null>(null)

  const { data: workspacesResult, isLoading: workspacesLoading } = useWorkspaces()

  const workspaces = workspacesResult?.success ? workspacesResult.data : []
  const adminWorkspaces = workspaces.filter(isWorkspaceAdmin)

  useEffect(() => {
    if (selectedWorkspaceId) return
    if (searchWorkspaceId) {
      setSelectedWorkspaceId(searchWorkspaceId)
      return
    }
    if (adminWorkspaces.length > 0) {
      setSelectedWorkspaceId(adminWorkspaces[0].id)
    }
  }, [adminWorkspaces, searchWorkspaceId, selectedWorkspaceId])

  const selectedWorkspace = workspaces.find((w) => w.id === selectedWorkspaceId) ?? null

  const subscriptionQuery = useQuery({
    queryKey: ['billing-subscription', selectedWorkspaceId],
    enabled: Boolean(selectedWorkspaceId),
    queryFn: async () => {
      const response = await getSubscription(selectedWorkspaceId!)
      if (!response.success) throw new Error(response.error.message)
      return response.data
    },
  })

  const subscription = subscriptionQuery.data

  async function handleCheckout(plan: PaidPlan) {
    if (!selectedWorkspaceId) return
    setCheckoutError(null)
    setCheckoutLoading(plan)

    const response = await initiateCheckout(selectedWorkspaceId, plan, billingCycle)
    setCheckoutLoading(null)

    if (!response.success) {
      setCheckoutError(response.error.message)
      return
    }

    window.location.href = response.data.checkoutLink
  }

  async function handleCancel() {
    if (!selectedWorkspaceId) return
    setActionLoading('cancel')
    setActionMessage(null)

    const response = await cancelSubscription(selectedWorkspaceId)
    setActionLoading(null)

    if (!response.success) {
      setActionMessage(response.error.message)
      return
    }

    setActionMessage('Subscription will cancel at the end of the current billing period.')
    await subscriptionQuery.refetch()
  }

  async function handleReactivate() {
    if (!selectedWorkspaceId) return
    setActionLoading('reactivate')
    setActionMessage(null)

    const response = await reactivateSubscription(selectedWorkspaceId)
    setActionLoading(null)

    if (!response.success) {
      setActionMessage(response.error.message)
      return
    }

    setActionMessage('Subscription reactivated.')
    await subscriptionQuery.refetch()
  }

  function handleWorkspaceChange(workspaceId: string) {
    setSelectedWorkspaceId(workspaceId)
    setActionMessage(null)
    setCheckoutError(null)
    navigate({ to: '/billing', search: { workspaceId } })
  }

  return (
    <AccountShell>
      <div className="mx-auto w-full max-w-2xl">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold tracking-tight">Billing</h1>
          <Text variant="caption" className="mt-1 block text-foreground/45">
            Manage your workspace plan and payment. All charges are in NGN (₦).
          </Text>
        </div>

        {workspacesLoading ? (
          <Text className="text-foreground/50">Loading workspaces…</Text>
        ) : adminWorkspaces.length === 0 ? (
          <SettingsSection
            id="billing-empty"
            title="No billable workspaces"
            description="You need to be a workspace admin to manage billing."
          >
            <Text className="text-foreground/60">
              Ask a workspace admin to upgrade, or create a new workspace from the home page.
            </Text>
          </SettingsSection>
        ) : (
          <div className="flex flex-col gap-6">
            <SettingsSection
              id="billing-workspace"
              title="Workspace"
              description="Select the workspace to manage."
            >
              <select
                value={selectedWorkspaceId ?? ''}
                onChange={(event) => handleWorkspaceChange(event.target.value)}
                className="w-full rounded-xl border border-foreground/10 bg-white px-3 py-2 text-sm"
              >
                {adminWorkspaces.map((workspace) => (
                  <option key={workspace.id} value={workspace.id}>
                    {workspace.name}
                  </option>
                ))}
              </select>
            </SettingsSection>

            {selectedWorkspace && subscription && (
              <>
                <CurrentPlanSection workspace={selectedWorkspace} subscription={subscription} />

                {(subscription.status === 'grace_period' || subscription.nombaLastChargeFailed) && (
                  <div className="rounded-xl border border-amber-300/60 bg-amber-50 px-4 py-3 text-sm text-amber-950">
                    Payment failed. Update your card to avoid downgrade
                    {subscription.graceEndsAt
                      ? ` by ${new Date(subscription.graceEndsAt).toLocaleDateString()}`
                      : ''}
                    .
                  </div>
                )}

                {subscription.cancelAtPeriodEnd && (
                  <div className="rounded-xl border border-foreground/10 bg-stone/40 px-4 py-3 text-sm text-foreground/70">
                    Cancellation scheduled
                    {subscription.currentPeriodEnd
                      ? ` for ${new Date(subscription.currentPeriodEnd).toLocaleDateString()}`
                      : ''}
                    .
                  </div>
                )}

                {actionMessage && (
                  <Text className="text-sm text-foreground/60">{actionMessage}</Text>
                )}

                {subscription.plan === 'starter' || subscription.status === 'unpaid' ? (
                  <UpgradeSection
                    billingCycle={billingCycle}
                    onBillingCycleChange={setBillingCycle}
                    checkoutError={checkoutError}
                    checkoutLoading={checkoutLoading}
                    onCheckout={handleCheckout}
                  />
                ) : subscription.plan === 'enterprise' ? (
                  <SettingsSection
                    id="billing-enterprise"
                    title="Enterprise plan"
                    description="Contact sales to change your Enterprise agreement."
                  >
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        window.location.href = `mailto:${salesEmail}`
                      }}
                    >
                      Contact sales
                    </Button>
                  </SettingsSection>
                ) : (
                  <SettingsSection
                    id="billing-manage"
                    title="Manage subscription"
                    description="Cancel or reactivate your plan."
                  >
                    <div className="flex flex-wrap gap-3">
                      {!subscription.cancelAtPeriodEnd ? (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={actionLoading !== null}
                          onClick={handleCancel}
                        >
                          {actionLoading === 'cancel' ? 'Cancelling…' : 'Cancel at period end'}
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={actionLoading !== null}
                          onClick={handleReactivate}
                        >
                          {actionLoading === 'reactivate' ? 'Reactivating…' : 'Reactivate'}
                        </Button>
                      )}
                    </div>
                  </SettingsSection>
                )}
              </>
            )}

            {subscriptionQuery.isLoading && selectedWorkspaceId && (
              <Text className="text-foreground/50">Loading subscription…</Text>
            )}
          </div>
        )}
      </div>
    </AccountShell>
  )
}

function CurrentPlanSection({
  workspace,
  subscription,
}: {
  workspace: Workspace
  subscription: Subscription
}) {
  const pricing = PLAN_PRICING[workspace.plan as keyof typeof PLAN_PRICING]

  return (
    <SettingsSection
      id="billing-current"
      title="Current plan"
      description="Your active subscription for this workspace."
    >
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex rounded-full border border-foreground/10 bg-stone px-3 py-1 text-sm font-medium capitalize">
            {formatPlanLabel(subscription.plan)}
          </span>
          <span className="text-sm text-foreground/50">
            {formatSubscriptionStatus(subscription.status)}
          </span>
        </div>
        <Text variant="caption" className="text-foreground/45">
          {pricing?.ngn ?? pricing?.usd ?? '—'}
          {subscription.billingCycle ? ` · Billed ${subscription.billingCycle}` : ''}
        </Text>
        {subscription.trialEndsAt && subscription.status === 'trialing' && (
          <Text variant="caption" className="text-foreground/45">
            Trial ends {new Date(subscription.trialEndsAt).toLocaleDateString()}
          </Text>
        )}
        {subscription.currentPeriodEnd && subscription.status !== 'trialing' && (
          <Text variant="caption" className="text-foreground/45">
            Current period ends {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
          </Text>
        )}
      </div>
    </SettingsSection>
  )
}

function UpgradeSection({
  billingCycle,
  onBillingCycleChange,
  checkoutError,
  checkoutLoading,
  onCheckout,
}: {
  billingCycle: BillingCycle
  onBillingCycleChange: (cycle: BillingCycle) => void
  checkoutError: string | null
  checkoutLoading: PaidPlan | null
  onCheckout: (plan: PaidPlan) => void
}) {
  return (
    <SettingsSection
      id="billing-upgrade"
      title="Upgrade plan"
      description="Your 14-day trial starts after your first payment. Cancel any time before it ends and you won't be charged again."
    >
      <div className="mb-4 flex gap-2">
        {(['monthly', 'annual'] as const).map((cycle) => (
          <button
            key={cycle}
            type="button"
            onClick={() => onBillingCycleChange(cycle)}
            className={cn(
              'rounded-full border px-4 py-1.5 text-sm capitalize transition-colors',
              billingCycle === cycle
                ? 'border-foreground bg-foreground text-white'
                : 'border-foreground/10 text-foreground/60 hover:text-foreground',
            )}
          >
            {cycle}
          </button>
        ))}
      </div>

      {checkoutError && <Text className="mb-3 text-sm text-red-600">{checkoutError}</Text>}

      <div className="grid gap-4 sm:grid-cols-2">
        {(['growth', 'business'] as const).map((plan) => {
          const amounts = PLAN_AMOUNTS_DISPLAY[plan]
          const amountKobo = billingCycle === 'annual' ? amounts.annualKobo : amounts.monthlyKobo
          const trialLabel = PLAN_PRICING[plan].trialLabel

          return (
            <div
              key={plan}
              className="flex flex-col gap-3 rounded-xl border border-foreground/10 p-4"
            >
              <div>
                <p className="font-medium capitalize">{formatPlanLabel(plan)}</p>
                <p className="mt-1 text-sm text-foreground/60">
                  {formatKoboAsNgn(amountKobo)}
                  {billingCycle === 'annual' ? '/yr' : '/mo'}
                </p>
                {trialLabel && (
                  <p className="mt-1 text-xs text-foreground/45">{trialLabel}</p>
                )}
                {billingCycle === 'annual' && (
                  <p className="mt-1 text-xs text-foreground/45">
                    Save {ANNUAL_SAVINGS[plan]} vs monthly
                  </p>
                )}
              </div>
              <Button
                size="sm"
                disabled={checkoutLoading !== null}
                onClick={() => onCheckout(plan)}
              >
                {checkoutLoading === plan ? 'Redirecting…' : `Upgrade to ${formatPlanLabel(plan)}`}
              </Button>
            </div>
          )
        })}
      </div>
    </SettingsSection>
  )
}
